<script setup lang="ts">
import { ref, watch } from "vue";
import { v4 as uuidv4 } from "uuid";
import InputLabel from "@/components/Reusables/Inputs/InputLabel.vue";

const props = defineProps<{
  label?: string;
  hideChevron?: boolean;
  options: any[];
  keepAfterSelect?: boolean;
  errorMsg?: string;
  required?: boolean;
  placeholder?: string;
  value: string;
}>();

const emits = defineEmits(["optionSelected"]);

const allOptions = ref<Array<any>>(props.options);
const options = ref<Array<any>>(props.options);
const isHidden = ref<boolean>(true);
const currentlyHoveredIndex = ref<number>(0);
const id = ref<string>(uuidv4());

const searchString = ref<string>(
  props.value === "All materials selected" || props.value === "All structures selected" ? "" : props.value
);

const getNewOptions = () => {
  const newOptions = allOptions.value.filter((option: any) => {
    if (typeof option === "string" && option.toLowerCase().includes(searchString.value.toLowerCase())) return true;
  });
  options.value = newOptions;
};

watch(searchString, (_newSearchString, _oldSearchString) => {
  getNewOptions();
});

const showOptions = () => {
  isHidden.value = false;
};
const hideOptions = () => {
  setTimeout(() => {
    isHidden.value = true;
  }, 200);
};

const handleNewSelected = (option: string) => {
  if (option === "All materials selected" || option === "All structures selected") return;
  searchString.value = option;

  if (option === undefined) return;
  if (option === null) return;

  emits("optionSelected", option);

  isHidden.value = true;
};

const nextItem = (event: any) => {
  isHidden.value = false;
  if (event.keyCode !== 38 && event.keyCode !== 40) return;
  if (event.keyCode === 38) {
    if (currentlyHoveredIndex.value <= 0) return;
    currentlyHoveredIndex.value--;
  } else {
    if (currentlyHoveredIndex.value >= options.value.length - 1) return;
    currentlyHoveredIndex.value++;
  }

  if (currentlyHoveredIndex.value % 4 === 0) {
    const focusedElement = document.getElementById(`${id.value}-${currentlyHoveredIndex.value}`);
    focusedElement?.scrollIntoView({ behavior: "smooth" });
  }
};
</script>

<template>
  <div class="w-full">
    <div class="flex flex-col">
      <InputLabel
        :id="id"
        :label="label"
        :required="required" />
      <div class="relative w-full">
        <input
          :id="id ? id : `input-${id}`"
          v-model="searchString"
          type="text"
          name="input"
          :class="[errorMsg ? 'border-app-red-500' : 'border-slate-500']"
          autocomplete="off"
          :placeholder="placeholder ? placeholder : undefined"
          class="focus:border-app-blue-600 rounded-8 h-9 w-full border p-2 text-sm leading-none outline-hidden"
          data-cy="search-select-input"
          @keyup="nextItem"
          @keyup.enter="handleNewSelected(options[currentlyHoveredIndex])"
          @focusin="showOptions"
          @focusout="hideOptions" />
        <font-awesome-icon
          v-if="!hideChevron"
          icon="fa-solid fa-chevron-down"
          class="absolute inset-y-1/2 right-2 -translate-y-1/2 cursor-pointer text-xs"
          @click="showOptions" />
      </div>
    </div>
    <span
      v-if="errorMsg"
      class="text-app-red-500 text-xs"
      >{{ errorMsg }}</span
    >
    <div
      v-if="!isHidden"
      class="relative">
      <div
        class="border-element-stroke rounded-8 bg-elevated shadow-dropdown absolute z-999 max-h-44 w-full overflow-y-scroll border">
        <div
          v-for="(option, index) in options"
          :id="`${id}-${index}`"
          :key="option"
          class="w-full cursor-pointer p-2 text-sm"
          :class="{
            'bg-surface': index === currentlyHoveredIndex && index !== 0,
            'pointer-events-none': option === 'All materials selected' || option === 'All structures selected',
          }"
          data-cy="search-select-option"
          @mouseenter="currentlyHoveredIndex = index"
          @click="handleNewSelected(option)">
          {{ option === "All materials selected" || option === "All structures selected" ? undefined : option }}
        </div>
      </div>
    </div>
  </div>
</template>
